/**
 * Quarter-turn rotation of a room's own data, for a room scanned or drawn at
 * the wrong cardinal orientation. A pure restructure: the four edges roll
 * around the rectangle and width and length swap, so nothing is resampled and
 * four turns give back the identical room.
 */
import { wrapRadiansUpToPi } from '../geometry';
import type { FloorPlanCorner, FloorPlanEdgeSegment, FloorPlanFreeElement, FloorPlanRoom } from '../schemas';

/** Corners are axis-based (dx along x, dy along y), and a quarter turn swaps the axes. */
const turnCorner = (corner: FloorPlanCorner | undefined): FloorPlanCorner | undefined => corner && { ...corner, dx: corner.dy, dy: corner.dx };

/** An alcove's chamfer legs are read along and across its parent edge by the edge's
 *  orientation, which flips under a quarter turn. Doors, windows and openings are unchanged. */
const turnSegment = (segment: FloorPlanEdgeSegment): FloorPlanEdgeSegment =>
  segment.edgeSegmentType !== 'alcove' ? segment : {
    ...segment,
    backLeftCorner: turnCorner(segment.backLeftCorner),
    backRightCorner: turnCorner(segment.backRightCorner),
  };

const withoutUndefined = <T extends object>(record: T): T => Object.fromEntries(Object.entries(record).filter(([, value]) => value !== undefined)) as T;

/** One clockwise quarter turn (in plan, y down): a point (x, y) moves to (L - y, x). */
function turnClockwise(room: FloorPlanRoom): FloorPlanRoom {
  const { width: W, length: L } = room;
  const turnEdge = (edge: FloorPlanRoom['backEdge']) => edge && { ...edge, segments: edge.segments.map(turnSegment) };
  const turnElement = (element: FloorPlanFreeElement): FloorPlanFreeElement =>
    withoutUndefined({
      ...element,
      x: L - (element.y + element.length),
      y: element.x,
      width: element.length,
      length: element.width,
      backLeftCorner: turnCorner(element.frontLeftCorner),
      backRightCorner: turnCorner(element.backLeftCorner),
      frontRightCorner: turnCorner(element.backRightCorner),
      frontLeftCorner: turnCorner(element.frontRightCorner),
    });
  // A placed room turns in place: its center stays put in the whole-property plan.
  const placed = room.x !== undefined && room.y !== undefined
    ? { x: room.x + (W - L) / 2, y: room.y + (L - W) / 2 }
    : {};
  return withoutUndefined({
    ...room,
    ...placed,
    width: L,
    length: W,
    backEdge: turnEdge(room.leftEdge),
    rightEdge: turnEdge(room.backEdge),
    frontEdge: turnEdge(room.rightEdge),
    leftEdge: turnEdge(room.frontEdge),
    backLeftCorner: turnCorner(room.frontLeftCorner),
    backRightCorner: turnCorner(room.backLeftCorner),
    frontRightCorner: turnCorner(room.backRightCorner),
    frontLeftCorner: turnCorner(room.frontRightCorner),
    freeElements: room.freeElements?.map(turnElement),
  });
}

/** North's bearing is kept to this many decimal places (picoradians), so turns that add up to a full circle give back the identical angle. */
const NORTH_ANGLE_PLACES = 1e12;

/** North is physically fixed, so measured from the new back edge it turns clockwise with the room: whole quarters added to the original bearing. */
const turnedNorth = (angle: number, turns: number): number =>
  Math.round(wrapRadiansUpToPi(angle + (turns * Math.PI) / 2) * NORTH_ANGLE_PLACES) / NORTH_ANGLE_PLACES;

/**
 * Rotate the room's data by whole quarter turns: positive is clockwise in the
 * plan, negative counterclockwise. A placed room keeps its center in the
 * whole-property plan, so it visibly turns there too.
 */
export function rotateRoomQuarterTurns(room: FloorPlanRoom, quarterTurns: number): FloorPlanRoom {
  const turns = ((Math.round(quarterTurns) % 4) + 4) % 4;
  let rotated = room;
  for (let turn = 0; turn < turns; turn++) rotated = turnClockwise(rotated);
  return turns === 0 || rotated.magneticNorthAngle === undefined ? rotated : { ...rotated, magneticNorthAngle: turnedNorth(rotated.magneticNorthAngle, turns) };
}
